import { ComponentType, lazy } from 'react';
import { SESSION_STORAGE_KEYS } from 'consts/sessionStorage';

/**
 * Returns true when the error looks like a missing content-hashed chunk (stale deployment 404).
 * @param err - The caught error value.
 * @returns Whether the error is a dynamic-import chunk-load failure.
 */
export const isChunkLoadError = (err: unknown): boolean =>
  err instanceof Error &&
  /failed to fetch dynamically imported module|loading chunk/i.test(
    err.message,
  );

/**
 * Wraps a dynamic-import factory with guarded chunk-reload recovery logic.
 * On import failure, reloads once (online check + sessionStorage one-shot guard) when
 * the error looks like a missing content-hashed chunk; rethrows all other errors.
 * @param factory - Async factory that imports a React component module.
 * @returns A new factory with the same signature that catches chunk-load failures.
 */
export const wrapLazyFactoryWithReload =
  <T extends ComponentType<object>>(
    factory: () => Promise<{ default: T }>,
  ): (() => Promise<{ default: T }>) =>
  () =>
    factory().catch((err: unknown) => {
      if (!isChunkLoadError(err) || !navigator.onLine) throw err;

      let shouldReload = true;

      try {
        if (sessionStorage.getItem(SESSION_STORAGE_KEYS.DID_CHUNK_RELOAD)) {
          shouldReload = false;
        } else {
          sessionStorage.setItem(SESSION_STORAGE_KEYS.DID_CHUNK_RELOAD, 'true');
        }
      } catch {
        // sessionStorage unavailable — reload without loop guard
      }

      if (!shouldReload) throw err;

      window.location.reload();

      return new Promise<never>(() => {});
    });

/**
 * Wraps `React.lazy` with guarded chunk-reload recovery for stale deployment 404s.
 * On import failure, reloads once (online check + sessionStorage one-shot guard) when
 * the error looks like a missing content-hashed chunk; rethrows all other errors to the ErrorBoundary.
 * @param factory - Async factory function passed to `React.lazy`.
 * @returns A lazy exotic component that auto-recovers from stale-chunk 404s.
 */
export const lazyWithReload = <T extends ComponentType<object>>(
  factory: () => Promise<{ default: T }>,
) => lazy(wrapLazyFactoryWithReload(factory));
